import { ButtonItem } from '@atlaskit/menu';
import { Box } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import { type OpenThreadContextData } from '@databases/comments/context/CommentContext';
import { type DialogContextInterface } from '@databases/dialog';
import React from 'react';

import {
  type MenuContentItemFactory,
  type MenuItemFactory,
  type SelectionsEffectMenuItem,
  type SeparatorItem,
} from '../select-value-menu/types';

export interface MenuSection {
  items: React.ReactNode[];
  hasSeparator: boolean;
}

export interface MenuSectionsResult {
  sections: MenuSection[];
  resolvedItems: SelectionsEffectMenuItem[];
}

function isSeparatorItem(item: MenuContentItemFactory): item is SeparatorItem {
  return typeof item === 'object' && 'type' in item && item.type === 'separator';
}

function isMenuItemFactory(item: MenuContentItemFactory): item is MenuItemFactory {
  return typeof item === 'function';
}

export function buildMenuSections(
  items: MenuContentItemFactory[],
  showDialog: DialogContextInterface['showDialog'],
  setOpenThread: (value: OpenThreadContextData) => void
): MenuSectionsResult {
  const resolvedItems: SelectionsEffectMenuItem[] = [];

  const sections = items.reduce<MenuSection[]>(
    (acc, item) => {
      const currentSection = acc[acc.length - 1];

      // if the current item is a separator and we have at least one item
      // this adds a separator. This ensures we never have a blank separator
      // on top of the menu
      if (isSeparatorItem(item) && currentSection.items.length > 0) {
        acc.push({ items: [], hasSeparator: true });
      } else if (isMenuItemFactory(item)) {
        const menuItem = item(showDialog, setOpenThread);

        if (menuItem) {
          resolvedItems.push(menuItem);

          const handleClick = (e: React.MouseEvent | React.KeyboardEvent) => {
            menuItem.onClick?.(e);
          };

          currentSection.items.push(
            <Box
              key={menuItem.key}
              onMouseEnter={menuItem.onMouseEnter}
              onMouseLeave={menuItem.onMouseLeave}
            >
              <Tooltip
                content={menuItem.tooltipDescription}
                position={menuItem.tooltipPosition}
              >
                <ButtonItem
                  role="menuitem"
                  iconBefore={menuItem.elemBefore}
                  iconAfter={menuItem.elemAfter}
                  isDisabled={menuItem.isDisabled}
                  onClick={handleClick}
                >
                  {menuItem.content}
                </ButtonItem>
              </Tooltip>
            </Box>
          );
        }
      }

      return acc;
    },
    [{ items: [], hasSeparator: false }]
  );

  return {
    sections: sections.filter((section) => section.items.length > 0),
    resolvedItems,
  };
}
